import * as React from 'react';
import { FC, memo } from 'react';

import { LoyaltyTierKey } from 'generated/graphql-gateway';
import { useFavoriteOffers } from 'hooks/favorite-offers';
import { useAuthContext } from 'state/auth';
import { selectors, useAppSelector } from 'state/global-state';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoyaltyUserTier } from 'state/loyalty/hooks/use-loyalty-user-tier';

import { parseExternalIncentiveData } from '../external-incentive-card/parse-external-incentive-data';
import { IncentiveDetailsWithAnimation } from '../loyalty-incentives.styled';

import { ExternalIncentiveDetailsView } from './external-incentive-details.view';
import { IExternalIncentiveDetailsProps } from './types';

/**
 *
 * IncentiveDetails displays an expanded view of a incentive when selected from the incentive list
 *
 */
const ExternalIncentiveDetails: FC<IExternalIncentiveDetailsProps> = ({ selectedIncentive }) => {
  const { loyaltyTierKey: userTier } = useLoyaltyUserTier();
  const { isAuthenticated } = useAuthContext();

  // True or null will close the offer to authenticated users only
  const offerRequiresAuthentication =
    selectedIncentive.authentificationRequired ||
    selectedIncentive.authentificationRequired === null;

  // If no loyalty tier is set for the offer,
  // then the default behaviour is the external offers showing for all Loyalty Tiers.
  const isOfferAvailableToUserTier =
    selectedIncentive.tierIncentiveList?.some(
      tierIncentive => tierIncentive?.tierName === userTier
    ) || !selectedIncentive.tierIncentiveList;

  // If the offer doesn't require authentication it always should be open
  const isOfferClosed =
    offerRequiresAuthentication && (!isAuthenticated() || !isOfferAvailableToUserTier);

  const rewardData = parseExternalIncentiveData(selectedIncentive, isOfferClosed);

  const enableFavoriteOffers = useFlag(LaunchDarklyFlag.ENABLE_FAVORITE_OFFERS);

  const loyaltyOffersInCooldown = useAppSelector(selectors.loyalty.selectLoyaltyOffersInCooldown);

  const { toggleFavorite, isFavorite, isOfferLoading } = useFavoriteOffers();

  const offerTier = selectedIncentive.tierIncentiveList?.[0]?.tierName as LoyaltyTierKey;

  return (
    <IncentiveDetailsWithAnimation>
      <ExternalIncentiveDetailsView
        {...rewardData}
        key={rewardData.id}
        offersInCooldown={loyaltyOffersInCooldown}
        favorited={enableFavoriteOffers && isFavorite(selectedIncentive._id || '')}
        favoriteLoading={isOfferLoading(selectedIncentive._id || '')}
        handleToggleFavorite={toggleFavorite}
        isOfferClosed={isOfferClosed}
        offerTier={offerTier}
        offerRequiresAuthentication={offerRequiresAuthentication}
      />
    </IncentiveDetailsWithAnimation>
  );
};

export default memo(ExternalIncentiveDetails);
